import {
  desiredSupportField,
  type PatientDetail,
} from "@care-by-ella/contracts";

// TES-1322 — her desired support (F-01 v11 SUIT_DESIRED_SUPPORT) as the
// doctor reads it. The F-01 doc: "Läkare och andra behöriga roller får se
// svaret som patientens uttryckta önskemål, men det ska vara visuellt
// separerat från medicinska riskflaggor och beslutsunderlag. Det får inte
// omvandlas till diagnos, journalpåstående eller medicinskt fynd." And:
// "Valet MEDICATION_SUPPORT är ett uttryckt stödbehov och får inte tolkas som
// önskemål om ett visst läkemedel eller som medicinsk indikation."
//
// Pure, so the wording is tested without a DOM. Nothing here reaches the AI
// readback, the flags or the journal prefill: those read the F-01 lift that
// withholds both keys (readback-input.ts, Ö-534). The one exception is the
// AI form overview behind its TES-1542 flag: the codes, never the "Annat" text.

export interface DoctorDesiredSupport {
  /** Her chosen options, in the words she was shown. */
  labels: string[];
  /** Her own words behind "Annat", when she wrote any. */
  other: string | null;
  /** "Från F-01 v11 (appen) — inlämnad 2026-10-13 09:41". */
  provenance: string;
}

export function desiredSupportLabel(code: string): string {
  const opt = (desiredSupportField().options ?? []).find(
    (o) => o.value === code,
  );
  return opt?.label.sv ?? code;
}

export function fmtSubmitted(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function doctorDesiredSupport(
  f01: PatientDetail["f01"] | undefined,
): DoctorDesiredSupport | null {
  const codes = f01?.desiredSupport ?? [];
  // v10 and earlier never asked: nothing is shown, not "inget svar".
  if (!f01 || codes.length === 0) return null;
  const version =
    f01.templateVersion !== null ? ` v${f01.templateVersion}` : "";
  const surface = f01.source === "lead" ? "webbens kartläggning" : "appen";
  const other = f01.desiredSupportOther ?? null;
  return {
    // With her own words present, "Annat" is shown as "Annat: …" instead.
    labels: codes
      .filter((c) => !(c === "OTHER" && other))
      .map(desiredSupportLabel),
    other,
    provenance: `Från F-01${version} (${surface}) — inlämnad ${fmtSubmitted(f01.submittedAt)}`,
  };
}
